/**
 * StatsManager.tsx - Statistics Configuration Management Component
 *
 * This component provides an interface for managing custom statistics in the
 * fundraiser builder with inline editing and reordering capabilities.
 *
 * Key Features:
 * - Custom statistics creation and editing
 * - Drag-and-drop reordering with visual feedback
 * - Inline editing with real-time validation
 * - Add, edit, and delete functionality
 * - Statistics preview and formatting
 * - Field validation and error handling
 *
 * Statistics Fields:
 * - Label: Descriptive name for the statistic
 * - Value: Numerical or text value to display
 * - Order: Drag-and-drop positioning for display
 *
 * Management Features:
 * - Add new statistics with default values
 * - Edit existing statistics inline
 * - Delete statistics with confirmation
 * - Reorder using drag-and-drop interface
 * - Real-time preview of statistics display
 *
 * Validation:
 * - Required field validation for labels and values
 * - Format validation for numerical values
 * - Duplicate label prevention
 * - Maximum statistics limit enforcement
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, drag-and-drop library, form validation
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Plus, X, GripVertical, Edit2 } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { CreateFundraiserModel } from "../../models";

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{
  isEditing: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  error: boolean;
}>`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid ${(props) => (props.isEditing ? "#3b82f6" : "#e2e8f0")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  opacity: ${(props) => (props.isDragging ? 0.7 : 1)};
  min-height: 140px;
  transform: ${(props) => {
    if (props.isDragging) return "rotate(3deg) scale(1.05)";
    if (props.isDropTarget) return "scale(1.02)";
    return "scale(1)";
  }};
  box-shadow: ${(props) => {
    if (props.isDragging) return "0 12px 24px rgba(0, 0, 0, 0.2)";
    if (props.isEditing) return "0 4px 12px rgba(59, 130, 246, 0.15)";
    return "0 2px 8px rgba(0, 0, 0, 0.05)";
  }};
  z-index: ${(props) => (props.isDragging ? 1000 : 1)};

  border-color: ${(props) => (props.error ? "#dc2626" : "")};

  &:hover {
    border-color: #3b82f6;
    transform: ${(props) =>
      props.isDragging
        ? "rotate(3deg) scale(1.05)"
        : "translateY(-2px) scale(1.02)"};
    box-shadow: ${(props) =>
      props.isDragging
        ? "0 12px 24px rgba(0, 0, 0, 0.2)"
        : "0 8px 16px rgba(0, 0, 0, 0.12)"};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DropZone = styled.div<{
  isActive: boolean;
  position: "before" | "after";
}>`
  position: absolute;
  ${(props) => (props.position === "before" ? "left: -8px;" : "right: -8px;")}
  top: 0;
  bottom: 0;
  width: 4px;
  background: ${(props) =>
    props.isActive
      ? "linear-gradient(180deg, #3b82f6, #1d4ed8)"
      : "transparent"};
  border-radius: 2px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  transform: ${(props) => (props.isActive ? "scaleY(1)" : "scaleY(0.3)")};
  box-shadow: ${(props) =>
    props.isActive ? "0 0 8px rgba(59, 130, 246, 0.6)" : "none"};
  z-index: 10;
`;

const DragGhost = styled.div<{ isDragging: boolean }>`
  position: fixed;
  top: -1000px;
  left: -1000px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 2px solid #3b82f6;
  border-radius: 12px;
  padding: 20px;
  min-height: 140px;
  width: 200px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: rotate(5deg) scale(1.1);
  z-index: 9999;
  pointer-events: none;
  opacity: ${(props) => (props.isDragging ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const DragHandle = styled.div<{ isDragging: boolean }>`
  cursor: grab;
  color: ${(props) => (props.isDragging ? "#3b82f6" : "#94a3b8")};
  padding: 4px;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  top: 8px;
  left: 8px;
  transform: ${(props) => (props.isDragging ? "scale(1.2)" : "scale(1)")};
  background: ${(props) =>
    props.isDragging ? "rgba(59, 130, 246, 0.1)" : "transparent"};

  &:hover {
    background: ${(props) =>
      props.isDragging ? "rgba(59, 130, 246, 0.2)" : "#f1f5f9"};
    color: ${(props) => (props.isDragging ? "#3b82f6" : "#64748b")};
    transform: scale(1.1);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.95);
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fecaca;
    transform: scale(1.05);
  }
`;

const StatLabelDisplay = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  text-align: center;
`;

const StatValueInput = styled.input<{ hasValue: boolean; isFocused: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  transition: border-color 0.2s ease;

  /* Hide placeholder when focused or has value */
  &::placeholder {
    color: ${(props) =>
      props.isFocused || props.hasValue ? "transparent" : "#94a3b8"};
    transition: color 0.2s ease;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const EditingSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input<{ hasValue?: boolean; isFocused?: boolean }>`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: border-color 0.2s ease;

  /* Hide placeholder when focused or has value */
  &::placeholder {
    color: ${(props) =>
      props.isFocused || props.hasValue ? "transparent" : "#9ca3af"};
    transition: color 0.2s ease;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const AddStatButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  min-height: 120px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
    transform: translateY(-1px);
  }
`;

const AddStatText = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

interface FundraiserStat {
  id: string;
  label: string;
  value: string;
  order: number;
}

interface StatsManagerProps {
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const StatsManager: React.FC<StatsManagerProps> = observer(
  ({ onKeyDown }) => {
    const { builderData } = fundraiserStore;
    const [editingStat, setEditingStat] = useState<string | null>(null);
    const [focusedFields, setFocusedFields] = useState<Record<string, boolean>>(
      {}
    );
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dropIndex, setDropIndex] = useState<number | null>(null);
    const [dragOverItem, setDragOverItem] = useState<string | null>(null);
    const [dropPosition, setDropPosition] = useState<"before" | "after" | null>(
      null
    );
    const containerRef = useRef<HTMLDivElement>(null);

    const stats = builderData.fundraiserStats || [];

    // Close editing stat when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setEditingStat(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const addStat = () => {
      const newStat: FundraiserStat = {
        id: Date.now().toString(),
        label: "New Stat",
        value: "",
        order: stats.length,
      };

      fundraiserStore.updateBuilderField("fundraiserStats", [
        ...stats,
        newStat,
      ]);
      setEditingStat(newStat.id);
    };

    const removeStatByIndex = (index: number) => {
      const updated = stats.filter((_, i) => i !== index);
      const reordered = updated.map((item, idx) => ({
        ...item,
        order: idx,
      }));
      fundraiserStore.updateBuilderField("fundraiserStats", reordered);
    };

    const updateStat = (id: string, field: string, value: string) => {
      const updated = stats.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      );
      fundraiserStore.updateBuilderField("fundraiserStats", updated);
    };

    const handleCardClick = (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      // Always show editing when clicking in the card
      if (editingStat !== id) {
        setEditingStat(id);
      }
    };

    const handleFieldFocus = (fieldId: string) => {
      setFocusedFields((prev) => ({ ...prev, [fieldId]: true }));
    };

    const handleFieldBlur = (fieldId: string) => {
      setFocusedFields((prev) => ({ ...prev, [fieldId]: false }));
    };

    const handleDragStart = (e: React.DragEvent, id: string) => {
      e.stopPropagation();

      // Create a custom drag image
      const dragElement = e.currentTarget.closest(
        "[data-stat-card]"
      ) as HTMLElement;
      if (dragElement) {
        const clone = dragElement.cloneNode(true) as HTMLElement;
        clone.style.transform = "rotate(5deg) scale(1.1)";
        clone.style.opacity = "0.9";
        clone.style.position = "absolute";
        clone.style.top = "-1000px";
        clone.style.left = "-1000px";
        clone.style.zIndex = "9999";
        document.body.appendChild(clone);

        // Use the clone as drag image
        e.dataTransfer.setDragImage(clone, 100, 70);

        // Clean up the clone after a short delay
        setTimeout(() => {
          document.body.removeChild(clone);
        }, 0);
      }

      setDraggedItem(id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    };

    const handleDragEnd = (e: React.DragEvent) => {
      e.stopPropagation();
      setDraggedItem(null);
      setDragOverItem(null);
      setDropPosition(null);
    };

    const handleDragOver = (
      e: React.DragEvent,
      targetId: string,
      index: number
    ) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";

      if (draggedItem && draggedItem !== targetId) {
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        const mouseX = e.clientX;

        // Determine if we're dropping before or after the current item
        const position = mouseX < midpoint ? "before" : "after";

        setDragOverItem(targetId);
        setDropPosition(position);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.stopPropagation();
      // Only clear if we're leaving the card container
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragOverItem(null);
        setDropPosition(null);
      }
    };

    const handleDrop = (
      e: React.DragEvent,
      targetId: string,
      index: number
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const draggedId = e.dataTransfer.getData("text/plain");
      if (!draggedId) return;

      const draggedIndex = stats.findIndex((item) => item.id === draggedId);
      if (draggedIndex === -1) return;

      let newIndex = index;

      // Adjust drop position based on where the mouse was
      if (dropPosition === "after") {
        newIndex = index + 1;
      }

      // Don't do anything if dropping in the same position
      if (
        draggedIndex === newIndex ||
        (draggedIndex + 1 === newIndex && dropPosition === "after")
      ) {
        setDraggedItem(null);
        setDragOverItem(null);
        setDropPosition(null);
        return;
      }

      const reordered = [...stats];
      const [draggedStat] = reordered.splice(draggedIndex, 1);

      // Adjust the insertion index if we removed an item before it
      const adjustedIndex = draggedIndex < newIndex ? newIndex - 1 : newIndex;
      reordered.splice(
        Math.max(0, Math.min(adjustedIndex, reordered.length)),
        0,
        draggedStat
      );

      // Update order values
      const updated = reordered.map((item, index) => ({
        ...item,
        order: index,
      }));
      fundraiserStore.updateBuilderField("fundraiserStats", updated);

      setDraggedItem(null);
      setDragOverItem(null);
      setDropPosition(null);
    };

    // Sort stats by order for consistent display
    const sortedStats = stats.slice().sort((a, b) => a.order - b.order);

    return (
      <StatsContainer ref={containerRef}>
        <DragGhost isDragging={!!draggedItem} />
        <StatsGrid>
          {sortedStats.map((stat, index) => (
            <StatCard
              key={stat.id}
              data-stat-card
              isEditing={editingStat === stat.id}
              isDragging={draggedItem === stat.id}
              isDropTarget={dragOverItem === stat.id}
              onClick={(e) => handleCardClick(e, stat.id)}
              onDragOver={(e) => handleDragOver(e, stat.id, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stat.id, index)}
              error={builderData.hasError(
                `fundraiserStats${index}` as keyof CreateFundraiserModel
              )}
            >
              <DropZone
                isActive={
                  dragOverItem === stat.id &&
                  dropPosition === "before" &&
                  draggedItem !== stat.id
                }
                position="before"
              />
              <DragHandle
                isDragging={draggedItem === stat.id}
                draggable
                onDragStart={(e) => handleDragStart(e, stat.id)}
                onDragEnd={handleDragEnd}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical size={14} />
              </DragHandle>
              <DropZone
                isActive={
                  dragOverItem === stat.id &&
                  dropPosition === "after" &&
                  draggedItem !== stat.id
                }
                position="after"
              />

              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeStatByIndex(index);
                }}
              >
                <X size={12} />
              </RemoveButton>

              <StatLabelDisplay>{stat.label}</StatLabelDisplay>
              <StatValueInput
                hasValue={!!stat.value}
                isFocused={focusedFields[`value-${stat.id}`] || false}
                type="text"
                placeholder="Stat Value"
                value={stat.value}
                onChange={(e) => updateStat(stat.id, "value", e.target.value)}
                onFocus={() => handleFieldFocus(`value-${stat.id}`)}
                onBlur={() => handleFieldBlur(`value-${stat.id}`)}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingStat(stat.id);
                }}
              />

              {editingStat === stat.id && (
                <EditingSection>
                  <FormGroup>
                    <Label>Stat Name</Label>
                    <Input
                      hasValue={!!stat.label && stat.label !== "New Stat"}
                      isFocused={focusedFields[`label-${stat.id}`] || false}
                      type="text"
                      value={stat.label}
                      onChange={(e) =>
                        updateStat(stat.id, "label", e.target.value)
                      }
                      placeholder="Stat Name"
                      onFocus={() => handleFieldFocus(`label-${stat.id}`)}
                      onBlur={() => handleFieldBlur(`label-${stat.id}`)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingStat(stat.id);
                      }}
                      onKeyDown={(e) => {
                        if (onKeyDown) {
                          onKeyDown(e, true);
                        }
                      }}
                    />
                  </FormGroup>
                </EditingSection>
              )}
            </StatCard>
          ))}

          <AddStatButton onClick={addStat}>
            <Plus size={24} />
            <AddStatText>Add Stat</AddStatText>
          </AddStatButton>
        </StatsGrid>
      </StatsContainer>
    );
  }
);
